/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import Select from 'react-select';
import { PlusCircle, Edit3, Trash2, Save, XCircle, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const statusTranslations = { /* Niepotrzebne tutaj, ale zostawiam jako wzór */ };
const getStatusClass = (status) => `status-${status?.toLowerCase().replace(/\s+/g, '-').replace(/_/, '-') || 'unknown'}`;

const reactSelectStyles = { /* Skopiuj style z QuoteForm.js lub innego komponentu admina */
    control: (provided, state) => ({ ...provided, borderColor: state.isFocused ? 'var(--color-accent)' : 'var(--color-border)', boxShadow: state.isFocused ? '0 0 0 2px rgba(108, 122, 137, 0.2)' : null, '&:hover': { borderColor: state.isFocused ? 'var(--color-accent)' : '#adb5bd', }, minHeight: 'calc(0.8rem * 2 + 0.6rem * 2 + 2px)', padding: '0.1rem 0.2rem',}),
    valueContainer: (provided) => ({ ...provided, padding: '0px 6px', }),
    input: (provided) => ({ ...provided, margin: '0px', padding: '0px', }),
    indicatorSeparator: () => ({ display: 'none', }),
    option: (provided, state) => ({ ...provided, backgroundColor: state.isSelected ? 'var(--color-accent)' : state.isFocused ? 'var(--color-bg-light-gray)' : null, color: state.isSelected ? 'var(--color-white)' : 'var(--color-text)', '&:active': { backgroundColor: 'var(--color-accent-darker)', }, }),
};

const SERVICE_TYPES_FOR_RATES = [ // Powinno być spójne z backendowym validatorem
    { value: 'translation_standard', label: 'Tłumaczenie Standard' },
    { value: 'translation_express', label: 'Tłumaczenie Ekspres' },
];

function AdminRatesPage() {
    const [rates, setRates] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [languageOptions, setLanguageOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('adminToken');

    const [isAdding, setIsAdding] = useState(false);
    const [newRate, setNewRate] = useState({ source_lang_code: '', target_lang_code: '', service_type: SERVICE_TYPES_FOR_RATES[0].value, rate_per_char: '', currency: 'PLN', description: '' });
    const [editingRate, setEditingRate] = useState(null);
    const [formError, setFormError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true); setError('');
        if (!token) { setError('Brak autoryzacji.'); setIsLoading(false); navigate('/admin/login'); return; }
        try {
            const [ratesRes, langsRes] = await Promise.all([
                axios.get(`${apiUrl}/api/admin/rates`, { headers: { 'x-auth-token': token } }),
                axios.get(`${apiUrl}/api/admin/languages`, { headers: { 'x-auth-token': token } }) // Pobieramy wszystkie, nie tylko aktywne, dla selectów
            ]);
            setRates(ratesRes.data);
            setLanguages(langsRes.data);
            const options = langsRes.data.map(lang => ({ value: lang.code, label: `${lang.name} (${lang.code})` }));
            setLanguageOptions(options);
        } catch (err) { setError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się pobrać danych stawek lub języków.'); if (err.response?.status === 401 || err.response?.status === 403) { navigate('/admin/login');} }
        finally { setIsLoading(false); }
    }, [apiUrl, navigate, token]);

    useEffect(() => { fetchAllData(); }, [fetchAllData]);

    const handleNewRateChange = (e) => { const { name, value } = e.target; setNewRate(prev => ({ ...prev, [name]: value })); setFormError(''); };
    const handleNewRateSelectChange = (selectedOption, actionMeta) => { const { name } = actionMeta; const value = selectedOption ? selectedOption.value : ''; setNewRate(prev => ({ ...prev, [name]: value })); setFormError(''); };
    
    const handleEditRateChange = (e) => { const { name, value } = e.target; setEditingRate(prev => ({ ...prev, [name]: value })); setFormError(''); };
    const handleEditRateSelectChange = (selectedOption, actionMeta) => { const { name } = actionMeta; const value = selectedOption ? selectedOption.value : ''; setEditingRate(prev => ({ ...prev, [name]: value })); setFormError(''); };

    const handleAddRate = async (e) => {
        e.preventDefault(); setFormError(''); setIsProcessing(true);
        if (!newRate.source_lang_code || !newRate.target_lang_code || !newRate.service_type || !newRate.rate_per_char) { setFormError('Wszystkie pola (oprócz opisu i waluty) są wymagane.'); setIsProcessing(false); return; }
        if (newRate.source_lang_code === newRate.target_lang_code) { setFormError('Język źródłowy i docelowy muszą być różne.'); setIsProcessing(false); return; }
        try {
            const payload = {...newRate, rate_per_char: parseFloat(newRate.rate_per_char) };
            await axios.post(`${apiUrl}/api/admin/rates`, payload, { headers: { 'x-auth-token': token } });
            setNewRate({ source_lang_code: '', target_lang_code: '', service_type: SERVICE_TYPES_FOR_RATES[0].value, rate_per_char: '', currency: 'PLN', description: '' }); setIsAdding(false); fetchAllData();
        } catch (err) { setFormError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się dodać stawki.'); }
        finally { setIsProcessing(false); }
    };

    const handleUpdateRate = async (e) => {
        e.preventDefault(); setFormError(''); setIsProcessing(true);
        if (!editingRate || !editingRate.rate_per_char) { setFormError('Stawka za znak jest wymagana.'); setIsProcessing(false); return; }
        try {
            const payload = {...editingRate, rate_per_char: parseFloat(editingRate.rate_per_char) };
            await axios.put(`${apiUrl}/api/admin/rates/${editingRate.id}`, payload, { headers: { 'x-auth-token': token } });
            setEditingRate(null); fetchAllData();
        } catch (err) { setFormError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się zaktualizować stawki.'); }
        finally { setIsProcessing(false); }
    };

    const handleDeleteRate = async (rateId) => {
        if (!window.confirm(`Czy na pewno chcesz usunąć stawkę ID: ${rateId}?`)) return;
        setIsProcessing(true); // Można dodać indywidualny stan ładowania dla wiersza
        try {
            await axios.delete(`${apiUrl}/api/admin/rates/${rateId}`, { headers: { 'x-auth-token': token } });
            fetchAllData();
        } catch (err) { alert(err.response?.data?.errors?.[0]?.msg || 'Nie udało się usunąć stawki.'); }
        finally { setIsProcessing(false); }
    };

    const startEdit = (rate) => { setEditingRate({ ...rate, rate_per_char: parseFloat(rate.rate_per_char).toFixed(4) }); setIsAdding(false); setFormError(''); };
    const cancelEdit = () => { setEditingRate(null); setFormError(''); };
    const startAdd = () => { setIsAdding(true); setNewRate({ source_lang_code: '', target_lang_code: '', service_type: SERVICE_TYPES_FOR_RATES[0].value, rate_per_char: '', currency: 'PLN', description: '' }); setEditingRate(null); setFormError(''); };
    const cancelAdd = () => { setIsAdding(false); setFormError(''); };

    if (isLoading) return <div className="container" style={{paddingTop: '2rem'}}>Ładowanie stawek...</div>;
    if (error && rates.length === 0) return <div className="container submit-message error" style={{paddingTop: '2rem'}}>{error}</div>;

    const currentFormRate = editingRate || newRate;
    const handleCurrentFormSelectChange = editingRate ? handleEditRateSelectChange : handleNewRateSelectChange;
    const handleCurrentFormInputChange = editingRate ? handleEditRateChange : handleNewRateChange;

    return (
        <section className="admin-page section-bg-white" style={{padding: "40px 0", minHeight: 'calc(100vh - var(--header-height) - var(--footer-height, 150px))'}}>
            <div className="container">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
                    <h1>Panel Administratora - Zarządzanie Stawkami</h1>
                    <Link to="/admin/dashboard" className="button-secondary button-small-outline">← Wróć do Dashboardu</Link>
                </div>

                {!isAdding && !editingRate && (
                    <button onClick={startAdd} className="button-primary" style={{marginBottom: '1.5rem'}}>
                        <PlusCircle size={18} style={{marginRight: '0.5rem'}}/> Dodaj Nową Stawkę
                    </button>
                )}

                {(isAdding || editingRate) && (
                    <form onSubmit={editingRate ? handleUpdateRate : handleAddRate} className="admin-edit-form" style={{marginBottom: '2rem'}}>
                        <h3>{editingRate ? `Edytuj Stawkę ID: ${editingRate.id}` : 'Dodaj Nową Stawkę'}</h3>
                        {formError && <div className="submit-message error form-message"><AlertCircle size={18}/> {formError}</div>}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="rateSourceLang">Język źródłowy</label>
                                <Select inputId="rateSourceLang" name="source_lang_code" options={languageOptions}
                                    value={languageOptions.find(opt => opt.value === currentFormRate.source_lang_code)}
                                    onChange={(opt) => handleCurrentFormSelectChange(opt, { name: 'source_lang_code' })}
                                    isDisabled={isProcessing || !!editingRate} styles={reactSelectStyles} placeholder="Wybierz..." />
                            </div>
                            <div className="form-group">
                                <label htmlFor="rateTargetLang">Język docelowy</label>
                                <Select inputId="rateTargetLang" name="target_lang_code" options={languageOptions}
                                    value={languageOptions.find(opt => opt.value === currentFormRate.target_lang_code)}
                                    onChange={(opt) => handleCurrentFormSelectChange(opt, { name: 'target_lang_code' })}
                                    isDisabled={isProcessing || !!editingRate} styles={reactSelectStyles} placeholder="Wybierz..." />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="rateServiceType">Typ Usługi</label>
                                <Select inputId="rateServiceType" name="service_type" options={SERVICE_TYPES_FOR_RATES}
                                    value={SERVICE_TYPES_FOR_RATES.find(opt => opt.value === currentFormRate.service_type)}
                                    onChange={(opt) => handleCurrentFormSelectChange(opt, { name: 'service_type' })}
                                    isDisabled={isProcessing || !!editingRate} styles={reactSelectStyles} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="ratePerChar">Stawka / znak (np. 0.0250)</label>
                                <input type="number" id="ratePerChar" name="rate_per_char" step="0.0001"
                                    value={currentFormRate.rate_per_char} onChange={handleCurrentFormInputChange}
                                    required disabled={isProcessing} />
                            </div>
                        </div>
                         <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="rateCurrency">Waluta</label>
                                <input type="text" id="rateCurrency" name="currency" maxLength="3"
                                    value={currentFormRate.currency} onChange={handleCurrentFormInputChange}
                                    disabled={isProcessing || !!editingRate} placeholder="PLN" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="rateDescription">Opis (opcjonalnie)</label>
                                <input type="text" id="rateDescription" name="description"
                                    value={currentFormRate.description} onChange={handleCurrentFormInputChange}
                                    maxLength="255" disabled={isProcessing} />
                            </div>
                        </div>
                        <div style={{marginTop: '1rem', display: 'flex', gap: '1rem'}}>
                            <button type="submit" className="button-primary" disabled={isProcessing}>
                                {isProcessing ? <Loader2 size={18} className="spinner"/> : <Save size={18}/>}
                                {editingRate ? 'Zapisz Zmiany' : 'Dodaj Stawkę'}
                            </button>
                            <button type="button" onClick={editingRate ? cancelEdit : cancelAdd} className="button-secondary" disabled={isProcessing}>Anuluj</button>
                        </div>
                    </form>
                )}

                {error && rates.length > 0 && <div className="submit-message error" style={{ marginBottom: '1rem'}}>{error}</div>}
                
                <div className="table-responsive-wrapper">
                    <table className="admin-table">
                        <thead><tr><th>ID</th><th>Z</th><th>Na</th><th>Usługa</th><th>Stawka/znak</th><th>Waluta</th><th>Opis</th><th>Akcje</th></tr></thead>
                        <tbody>
                            {rates.map(rate => (
                                <tr key={rate.id}>
                                    <td>{rate.id}</td>
                                    <td>{rate.source_lang_name} ({rate.source_lang_code})</td>
                                    <td>{rate.target_lang_name} ({rate.target_lang_code})</td>
                                    <td>{SERVICE_TYPES_FOR_RATES.find(s => s.value === rate.service_type)?.label || rate.service_type}</td>
                                    <td>{parseFloat(rate.rate_per_char).toFixed(4)}</td>
                                    <td>{rate.currency}</td>
                                    <td>{rate.description || '-'}</td>
                                    <td>
                                        <button onClick={() => startEdit(rate)} className="button-small-outline" style={{marginRight: '0.5rem'}} disabled={isProcessing}><Edit3 size={14}/> Edytuj</button>
                                        <button onClick={() => handleDeleteRate(rate.id)} className="button-small-outline button-danger" disabled={isProcessing}><Trash2 size={14}/> Usuń</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}
export default AdminRatesPage;